//React
import React, { useId } from "react";
import PropTypes from "prop-types";
import { fractionalIndex } from "../../utilities/fractionalIndex";
//CSS
import "./inputs.scss";

// Thumb diameter, $thumb in inputs.scss (.input-wrapper.slider).
const THUMB = 20;

/**
 * Range slider (ASB-3953), replacing the one MUI Slider in the app. A native
 * <input type="range"> styled to look exactly like the MUI slider it replaces
 * (2px track, 20px thumb with a hover halo, value bubble in the slider color,
 * marks on the track), with the two extras MUI gave us: discrete `marks` (the
 * thumb snaps to them) and a value label above the thumb. Pass `marks` to snap to a fixed set of values, or `min`/`max`/`step`
 * for a continuous range.
 *
 * Opt-in extras (ASB-2995), all off by default so existing sliders are unchanged:
 * `tone="primary"` paints it in the primary colour instead of the secondary;
 * `markLabels` writes formatLabel(mark) under each mark; `markInset` puts the first
 * and last mark's centres { left, right } px in from the slider's edges, to line the
 * marks up with something drawn above it, such as a chart's x axis; `valueStem` draws
 * a dashed line from the thumb up to the slider's top edge, continuing a marker drawn on
 * whatever sits directly above.
 */
function Slider({
	name,
	label,
	value,
	onChange,
	min,
	max,
	step,
	marks,
	disabled,
	formatLabel,
	showValue,
	showMarks,
	tone,
	markLabels,
	markInset,
	valueStem,
}) {
	const autoId = useId();
	const id = name ?? autoId;
	const discrete = Array.isArray(marks) && marks.length > 0;
	const sorted = discrete ? [...marks].sort((a, b) => a - b) : null;
	const lo = discrete ? 0 : min ?? 0;
	const hi = discrete ? sorted.length - 1 : max ?? 100;
	const exact = discrete ? sorted.indexOf(value) : -1;
	// A value that is not one of the marks (a saved 72 among 60, 65 ... 95) sits where it
	// belongs, between its neighbours, or pinned to the nearer end when outside them. The
	// first touch snaps it to a mark; from there it moves mark to mark as usual.
	const offMark = discrete && exact < 0 && value != null;
	const idx = discrete ? (offMark ? fractionalIndex(sorted, value) : exact) : null;
	const position = discrete ? idx : value;
	const pct = hi === lo ? 0 : ((position - lo) / (hi - lo)) * 100;
	const fmt = formatLabel ?? ((v) => String(v));

	const handle = (e) => {
		const n = Number(e.target.value);
		const next = discrete ? sorted[Math.min(hi, Math.max(0, Math.round(n)))] : n;
		if (onChange && next !== value) onChange(next);
	};

	// Off a mark the input's step is "any" (so the thumb can sit between marks), which
	// would make an arrow key move a fraction. Step to the next mark in that direction.
	// A value pinned outside the marks (97 shown at 95) has no next mark further out:
	// that arrow leaves it alone rather than moving it the other way.
	const keyOffMark = (e) => {
		if (!offMark) return;
		const up = e.key === "ArrowRight" || e.key === "ArrowUp";
		const down = e.key === "ArrowLeft" || e.key === "ArrowDown";
		if (!up && !down) return;
		e.preventDefault();
		const next = sorted[up ? Math.ceil(idx) : Math.floor(idx)];
		if (up ? next <= value : next >= value) return;
		if (onChange) onChange(next);
	};

	// The native input puts 0% / 100% on the thumb centres, half a thumb in from its
	// ends, so a mark centre `inset` px in means the input starts `inset - THUMB / 2` in.
	const pad = (inset) => (inset == null ? undefined : Math.max(0, inset - THUMB / 2));
	const insetStyle = markInset
		? { paddingLeft: pad(markInset.left), paddingRight: pad(markInset.right) }
		: undefined;

	return (
		<div
			className={[
				"input-wrapper slider",
				disabled ? "disabled" : "enabled",
				tone === "primary" ? "slider--primary" : "",
				valueStem ? "slider--stem" : "",
			]
				.join(" ")
				.trim()}
			style={insetStyle}
		>
			{label ? <label htmlFor={id}>{label}</label> : ""}
			<div className="slider-track-wrap">
				<div
					className="slider-overlay"
					aria-hidden="true"
				>
					{valueStem ? (
						<span
							className="slider-stem"
							style={{ left: `${pct}%` }}
						/>
					) : null}
					{discrete && (showMarks ?? true)
						? sorted.map((m, i) => (
								<span
									key={m}
									className={["slider-mark", i <= idx ? "slider-mark--active" : ""]
										.join(" ")
										.trim()}
									style={{ left: `${hi === lo ? 0 : (i / (hi - lo)) * 100}%` }}
								/>
						  ))
						: null}
					{showValue ?? true ? (
						<output
							htmlFor={id}
							className="slider-value"
							style={{ left: `${pct}%` }}
						>
							{fmt(value)}
						</output>
					) : null}
				</div>
				<input
					id={id}
					name={name}
					type="range"
					className="slider-input"
					min={lo}
					max={hi}
					step={discrete ? (offMark ? "any" : 1) : step ?? 1}
					value={position}
					disabled={disabled ?? false}
					aria-valuetext={fmt(value)}
					onChange={handle}
					onKeyDown={keyOffMark}
					style={{ "--slider-fill": `${pct}%` }}
				/>
			</div>
			{discrete && markLabels ? (
				<div
					className="slider-mark-labels"
					aria-hidden="true"
				>
					{sorted.map((m, i) => (
						<span
							key={m}
							className={["slider-mark-label", m === value ? "slider-mark-label--active" : ""]
								.join(" ")
								.trim()}
							style={{ left: `${hi === lo ? 0 : (i / (hi - lo)) * 100}%` }}
						>
							{fmt(m)}
						</span>
					))}
				</div>
			) : null}
		</div>
	);
}

Slider.propTypes = {
	name: PropTypes.string,
	label: PropTypes.string,
	value: PropTypes.number.isRequired,
	/** (value: number) => void */
	onChange: PropTypes.func,
	min: PropTypes.number,
	max: PropTypes.number,
	step: PropTypes.number,
	/** discrete allowed values; the thumb snaps between them */
	marks: PropTypes.arrayOf(PropTypes.number),
	disabled: PropTypes.bool,
	/** (value) => string shown above the thumb; default String(value) */
	formatLabel: PropTypes.func,
	showValue: PropTypes.bool,
	showMarks: PropTypes.bool,
	/** "primary" paints the slider in the primary colour; default is the secondary */
	tone: PropTypes.oneOf(["secondary", "primary"]),
	/** write formatLabel(mark) under each mark (discrete sliders only) */
	markLabels: PropTypes.bool,
	/** px from the slider's left / right edges to the first / last mark's centre */
	markInset: PropTypes.shape({ left: PropTypes.number, right: PropTypes.number }),
	/** a dashed stem from the thumb up to the slider's top edge */
	valueStem: PropTypes.bool,
};

export default Slider;
